/**
 * Personal bot-defaults card (personal Settings). A trader's overrides on top
 * of the org baseline; renders only when the trader has bot access. Saves via
 * PATCH /api/trader/me/bot-defaults, then refreshMe() to update the session.
 */
import { useEffect, useState } from 'react';
import { Bot } from 'lucide-react';
import { apiFetch, useAuthStore } from '@/stores/authStore';
import { useBotAccess } from '@/hooks/useBotAccess';
import { useGrantPendingReason, useGrantSlot } from '@/hooks/useGrantSlot';
import type { BotDefaults, BotKindConfig } from '@/lib/bots/botDefaults';
import { BotDefaultsEditor, cloneByKind } from '@/components/bots/BotDefaultsEditor';

type ByKind = Partial<Record<string, BotKindConfig>>;

export function BotDefaultsPanel() {
  const botAccess = useBotAccess();
  // A restored grant keeps the panel in place, Save disabled, until confirmed.
  const slot = useGrantSlot('botAccess');
  const pendingReason = useGrantPendingReason();
  const stored = useAuthStore((s) => s.user?.botDefaults);
  const refreshMe = useAuthStore((s) => s.refreshMe);
  const [byKind, setByKind] = useState<ByKind>(() => cloneByKind(stored?.byKind));
  const [dirty, setDirty] = useState(false);
  // A rejected field never reaches `byKind`, so Save would otherwise stay
  // enabled on the strength of an earlier valid edit and quietly save something
  // other than what the trader is looking at.
  const [editorValid, setEditorValid] = useState(true);
  const [saving, setSaving] = useState(false);
  // #1113: 'saved-stale' = the PATCH worked but this tab's profile re-read did
  // not apply, so the editor shows the previous defaults until a reload.
  const [saved, setSaved] = useState<'idle' | 'saved' | 'saved-stale'>('idle');
  const [error, setError] = useState<string | null>(null);

  // Re-seed from the store when it changes, unless there are unsaved edits.
  useEffect(() => {
    if (!dirty) setByKind(cloneByKind(stored?.byKind));
  }, [stored, dirty]);

  if (slot === 'hidden') return null;

  const save = async () => {
    if (!botAccess) return;
    setSaving(true);
    setError(null);
    setSaved('idle');
    try {
      const body: BotDefaults = { ...(stored ?? {}), byKind };
      const res = await apiFetch('/api/trader/me/bot-defaults', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ botDefaults: body }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? 'Save failed');
        return;
      }
      // refreshMe FIRST, then clear dirty. Clearing dirty before refreshMe
      // resolves would let the re-seed effect fire against the still-stale store
      // and flicker the editor back to pre-save values mid-await.
      // On `false` the re-seed below repaints the previous values, which is
      // what the stale line says, and Save stays disabled until the next edit.
      const applied = await refreshMe();
      setDirty(false);
      setSaved(applied ? 'saved' : 'saved-stale');
    } catch {
      setError('Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-card border-border rounded-lg border p-5">
      <h2 className="mb-1 flex items-center gap-2 text-base font-medium">
        <Bot size={16} className="text-muted-foreground" /> Bot defaults
      </h2>
      <p className="text-muted-foreground mb-3 text-xs">
        Your personal defaults for deploying bots. These override the org
        baseline; anything left blank inherits it.
      </p>
      <BotDefaultsEditor
        byKind={byKind}
        scope="personal"
        onValidityChange={setEditorValid}
        onChange={(next) => {
          setByKind(next);
          setDirty(true);
          setSaved('idle');
        }}
      />
      <div className="mt-3 flex items-center gap-3">
        <button
          onClick={save}
          disabled={!botAccess || saving || !dirty || !editorValid}
          title={botAccess ? undefined : pendingReason}
          className="bg-primary text-primary-foreground rounded px-3 py-1.5 text-xs font-medium disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save defaults'}
        </button>
        {saved === 'saved' && !dirty && <span className="text-xs text-emerald-500">Saved</span>}
        {saved === 'saved-stale' && !dirty && (
          <span className="text-muted-foreground flex items-center gap-2 text-xs" role="status" data-testid="bot-defaults-saved-stale">
            Saved. This tab still shows your previous defaults until it reloads
            <button type="button" onClick={() => window.location.reload()}
              className="border-border hover:bg-muted rounded border px-2 py-0.5 text-xs font-medium">
              Reload
            </button>
          </span>
        )}
        {error && <span className="text-xs text-red-400">{error}</span>}
      </div>
    </div>
  );
}
